// "Escalate to on-call support": POST /api/v1/support/escalations (KAT-8489). Each
// success pages fastn's on-call engineer, so the form mints one idempotency key
// per submission and reuses it on retry — a double click or a retried request
// is answered with the first incident instead of paging twice.

import { useMutation } from "@tanstack/react-query";
import { capture } from "@/lib/posthog";
import { apiFetch, post } from "@/lib/api";
import { API_BASE } from "@/lib/utils";

export type EscalationImpact = "outage" | "degraded" | "data" | "security";

export interface SupportEscalationInput {
  summary: string;
  impact?: EscalationImpact;
  details: string;
  startedAt?: string;
  affected?: string;
  callbackPhone?: string;
  /** Page the customer was on (origin + path only) and their time zone: context for the engineer. */
  pageUrl?: string;
  timezone?: string;
  idempotencyKey: string;
}

export interface SupportEscalationReceipt {
  escalationId: string;
  /** Spike's incident number when Spike returned one. Internal; the customer is shown escalationId. */
  incident: string | null;
  pagedAt: string;
  /** An Intercom ticket (or, if Intercom refused the ticket, a conversation) was opened: the engineer replies there. */
  conversationLinked: boolean;
  /** Its conversation id. A ticket's API id is also its conversation id. */
  conversationId: string | null;
  /** Set when a ticket was opened, to open Messenger straight on the ticket. */
  ticketId: string | null;
}

export function useCreateSupportEscalation() {
  return useMutation<SupportEscalationReceipt, Error, SupportEscalationInput>({
    mutationFn: (body) =>
      apiFetch<SupportEscalationReceipt>(`${API_BASE}/v1/support/escalations`, post(body)),
    onSuccess: (_data, body) => {
      capture("support_escalation_submitted", { impact: body.impact ?? "unspecified" });
    },
  });
}
